import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Mail, Sparkles,
  TrendingUp, ShieldCheck, Star, Info, Lightbulb, Users,
} from "lucide-react";
import Topbar from "./Topbar";
import "../quiz.css";
import {
  QUIZ_INTRO, QUESTIONS, PILLARS, computeQuizResult,
} from "../lib/quizData";
import useRouteMeta from "../lib/useRouteMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESET_CTA_URL =
  process.env.REACT_APP_RESET_CTA_URL ||
  "https://finlightenment.mykajabi.com/Financial-foundation-reset-priority-list";

// Stages: 'intro' -> 'quiz' -> 'gate' -> 'results'
export default function Quiz() {
  useRouteMeta("quiz");
  const [stage, setStage] = useState("intro");
  const [answers, setAnswers] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  // 'Did you know' feedback reveal state — letter that was just picked, or null
  const [revealLetter, setRevealLetter] = useState(null);
  // Aggregate quiz stats (for "compare to others" social proof). Loaded once.
  const [quizStats, setQuizStats] = useState(null);

  // Persist progress so refresh doesn't lose answers
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("fq_state") || "null");
      if (stored?.answers) setAnswers(stored.answers);
      if (stored?.qIndex && Number.isInteger(stored.qIndex)) setQIndex(stored.qIndex);
      if (stored?.stage && ["intro", "quiz"].includes(stored.stage)) setStage(stored.stage);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (stage === "intro" || stage === "quiz") {
      try {
        localStorage.setItem("fq_state", JSON.stringify({ stage, answers, qIndex }));
      } catch (e) { /* ignore */ }
    }
  }, [stage, answers, qIndex]);

  const question = QUESTIONS[qIndex];
  const allAnswered = QUESTIONS.every((q) => answers[q.id]);
  const liveResult = useMemo(() => computeQuizResult(answers), [answers]);

  const startQuiz = () => {
    setStage("quiz");
    setQIndex(0);
    // Fetch aggregate stats once when the user begins
    if (!quizStats) {
      axios.get(`${API}/quiz/stats`)
        .then((res) => setQuizStats(res.data))
        .catch(() => { /* silent — stats are nice-to-have, not required */ });
    }
  };

  const pickOption = (letter) => {
    // Set the answer and reveal the "Did you know" feedback.
    // User must click the Next question button to advance — no auto-timer
    // so they can take their time reading the insight + social proof.
    setAnswers((prev) => ({ ...prev, [question.id]: letter }));
    setRevealLetter(letter);
  };

  const advance = () => {
    setRevealLetter(null);
    if (qIndex < QUESTIONS.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      setStage("gate");
    }
  };

  const goBack = () => {
    setRevealLetter(null);
    if (qIndex > 0) setQIndex(qIndex - 1);
  };

  const updateLead = (k) => (e) => {
    setLead((p) => ({ ...p, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validateGate = () => {
    const er = {};
    if (!lead.name.trim()) er.name = "Please enter your name.";
    if (!lead.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email))
      er.email = "Please enter a valid email.";
    if (!lead.phone.trim()) er.phone = "Please enter your phone number.";
    else if (lead.phone.replace(/\D/g, "").length < 7)
      er.phone = "Please enter a valid phone number.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submitQuiz = async (e) => {
    e?.preventDefault();
    if (!validateGate() || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        name: lead.name.trim(),
        email: lead.email.trim().toLowerCase(),
        phone: lead.phone.trim(),
        answers,
      });
      setServerResult(res.data);
      setStage("results");
      try { localStorage.removeItem("fq_state"); } catch (e) { /* ignore */ }
      // Smooth scroll to top for the results reveal
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setToast({ msg: detail, kind: "error" });
      setSubmitting(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  const restart = () => {
    setAnswers({});
    setQIndex(0);
    setLead({ name: "", email: "", phone: "" });
    setServerResult(null);
    setErrors({});
    setStage("intro");
    try { localStorage.removeItem("fq_state"); } catch (e) { /* ignore */ }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="fq-app" data-testid="fq-app">
      <Topbar />
      <div className="fq-wrap">
        {stage === "intro" && <IntroCard onStart={startQuiz} />}

        {stage === "quiz" && (
          <QuestionCard
            question={question}
            qIndex={qIndex}
            total={QUESTIONS.length}
            selected={answers[question.id]}
            revealLetter={revealLetter}
            onPick={pickOption}
            onBack={goBack}
            onContinue={advance}
            quizStats={quizStats}
          />
        )}

        {stage === "gate" && (
          <GateCard
            lead={lead}
            errors={errors}
            submitting={submitting}
            onChange={updateLead}
            onSubmit={submitQuiz}
            onBack={() => { setStage("quiz"); setQIndex(QUESTIONS.length - 1); }}
            allAnswered={allAnswered}
            previewTier={liveResult.tier}
          />
        )}

        {stage === "results" && serverResult && (
          <ResultsView result={serverResult} lead={lead} onRestart={restart} />
        )}
      </div>

      {toast && (
        <div className={`fq-toast ${toast.kind === "error" ? "error" : ""}`} data-testid="fq-toast">
          {toast.msg}
        </div>
      )}
    </div>
  );
}

/* =================================================================== */
/*                          Intro card                                  */
/* =================================================================== */
function IntroCard({ onStart }) {
  return (
    <div className="fq-card fq-fade-in" data-testid="fq-intro">
      <span className="fq-eyebrow">{QUIZ_INTRO.badge}</span>
      <h1 className="fq-headline">{QUIZ_INTRO.headline}</h1>
      <p className="fq-sub">{QUIZ_INTRO.subhead}</p>

      {QUIZ_INTRO.body.map((para, i) => (
        <p key={i} className="fq-sub">{para}</p>
      ))}

      <div className="fq-pillars" data-testid="fq-pillars">
        {QUIZ_INTRO.pillars.map((p) => (
          <div className="fq-pillar-chip" key={p}>{p}</div>
        ))}
      </div>

      <p className="fq-sub" style={{ marginBottom: 28 }}><em>{QUIZ_INTRO.closing}</em></p>

      <div className="fq-cta-row">
        <button className="fq-btn" onClick={onStart} data-testid="fq-start">
          Start the 90-second check <ArrowRight size={18} />
        </button>
        <span className="fq-trust">15 questions · <strong>~90 seconds</strong> · Free results</span>
      </div>
    </div>
  );
}

/* =================================================================== */
/*                          Question card                               */
/* =================================================================== */
function QuestionCard({ question, qIndex, total, selected, revealLetter, onPick, onBack, onContinue, quizStats }) {
  const pct = ((qIndex + 1) / total) * 100;
  const revealedOption = revealLetter
    ? question.options.find((o) => o.letter === revealLetter)
    : null;
  const revealing = !!revealedOption;
  const isLast = qIndex === total - 1;

  // Build the social-proof message based on aggregate stats
  const socialProof = useMemo(() => {
    if (!revealing || !quizStats) return null;
    const minSample = quizStats.min_sample ?? 5;
    const qBucket = quizStats.by_question?.[String(question.id)];
    if (!qBucket || (qBucket.total || 0) < minSample) return null;

    const sameCount = qBucket[revealLetter] ?? 0;
    const samePct = Math.round((sameCount / qBucket.total) * 100);
    if (samePct <= 0) return null;

    // Pick the message tone based on what they chose (score) and majority
    const score = revealedOption?.score ?? 1;
    const counts = [qBucket.A || 0, qBucket.B || 0, qBucket.C || 0];
    const maxCount = Math.max(...counts);
    const isMajority = sameCount === maxCount;

    let label;
    if (score === 2 && !isMajority && samePct <= 50) {
      label = `Top tier — only ${samePct}% of people picked this answer.`;
    } else if (score === 0 && isMajority) {
      label = `You're not alone — ${samePct}% of people answered the same way.`;
    } else if (score === 0) {
      label = `${samePct}% of people answered the same way — and the next step is the same for all of them.`;
    } else if (score === 2 && isMajority) {
      label = `${samePct}% of people picked this — you're with the strongest group.`;
    } else {
      label = `${samePct}% of people answered the same way.`;
    }
    return label;
  }, [revealing, quizStats, question.id, revealLetter, revealedOption]);

  return (
    <div className="fq-card fq-fade-in" data-testid="fq-question-card">
      <div className="fq-progress-row">
        <span data-testid="fq-progress-text">Question {qIndex + 1} of {total}</span>
        <span>{Math.round(pct)}% complete</span>
      </div>
      <div className="fq-progress-track">
        <div className="fq-progress-fill" style={{ width: `${pct}%` }} data-testid="fq-progress-fill" />
      </div>

      <div className="fq-question">
        <div className="fq-pillar-label" data-testid="fq-pillar-label">
          {PILLARS[question.pillar]?.label}
        </div>
        <h2 className="fq-q-text" data-testid={`fq-q-${question.id}`}>{question.prompt}</h2>

        <div className="fq-options">
          {question.options.map((opt) => {
            const isSelected = selected === opt.letter;
            const isThisRevealed = revealing && revealLetter === opt.letter;
            const dimmed = revealing && revealLetter !== opt.letter;
            return (
              <button
                key={opt.letter}
                type="button"
                className={`fq-option ${isSelected ? "selected" : ""} ${isThisRevealed ? "revealed" : ""} ${dimmed ? "dimmed" : ""}`}
                onClick={() => onPick(opt.letter)}
                disabled={revealing}
                data-testid={`fq-opt-${question.id}-${opt.letter}`}
              >
                <span className="fq-option-letter">{opt.letter}</span>
                <span className="fq-option-text">{opt.text}</span>
              </button>
            );
          })}
        </div>

        {/* Did-you-know feedback reveal */}
        {revealing && (
          <div className="fq-reveal" data-testid="fq-reveal">
            <div className="fq-reveal-head">
              <span className="fq-reveal-icon"><Lightbulb size={18} strokeWidth={2.5} /></span>
              <span className="fq-reveal-kicker">Did you know…</span>
            </div>
            <p className="fq-reveal-text" data-testid="fq-reveal-text">
              {revealedOption.feedback}
            </p>

            {socialProof && (
              <div className="fq-social-proof" data-testid="fq-social-proof">
                <Users size={15} strokeWidth={2.4} />
                <span>{socialProof}</span>
              </div>
            )}

            <div className="fq-reveal-actions">
              <button
                type="button"
                className="fq-btn fq-btn-gold fq-reveal-continue"
                onClick={onContinue}
                data-testid="fq-reveal-continue"
              >
                {isLast ? "See my results" : "Next question"} <ArrowRight size={16} />
              </button>
              <span className="fq-reveal-auto">Take your time — click Next when you're ready.</span>
            </div>
          </div>
        )}

        <div className="fq-actions">
          <button className="fq-back" onClick={onBack} disabled={qIndex === 0} data-testid="fq-back">
            <ArrowLeft size={16} /> Back
          </button>
          {!revealing && (
            <span className="fq-trust">Click an answer to continue →</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* =================================================================== */
/*                          Email gate (after quiz)                     */
/* =================================================================== */
function GateCard({ lead, errors, submitting, onChange, onSubmit, onBack, allAnswered, previewTier }) {
  return (
    <div className="fq-card fq-fade-in fq-gate" data-testid="fq-gate">
      <div className="fq-gate-icon"><Sparkles size={28} strokeWidth={2.5} /></div>
      <h2 className="fq-gate-headline">Your results are ready.</h2>
      <p className="fq-gate-sub">
        Tell us where to send your <strong>personalized PDF report</strong> — including your tier,
        score across all 6 pillars, and your specific next step. We'll show your results instantly.
      </p>

      <form className="fq-form" onSubmit={onSubmit} noValidate>
        <div className="fq-form-group">
          <label htmlFor="fq-name">Full name</label>
          <input
            id="fq-name"
            type="text"
            className={`fq-input ${errors.name ? "error" : ""}`}
            placeholder="Jane Smith"
            value={lead.name}
            onChange={onChange("name")}
            autoComplete="name"
            data-testid="fq-input-name"
          />
          {errors.name && <div className="fq-form-error">{errors.name}</div>}
        </div>

        <div className="fq-form-group">
          <label htmlFor="fq-email">Email address</label>
          <input
            id="fq-email"
            type="email"
            className={`fq-input ${errors.email ? "error" : ""}`}
            placeholder="you@example.com"
            value={lead.email}
            onChange={onChange("email")}
            autoComplete="email"
            data-testid="fq-input-email"
          />
          {errors.email && <div className="fq-form-error">{errors.email}</div>}
        </div>

        <div className="fq-form-group">
          <label htmlFor="fq-phone">Phone number</label>
          <input
            id="fq-phone"
            type="tel"
            className={`fq-input ${errors.phone ? "error" : ""}`}
            placeholder="(555) 123-4567"
            value={lead.phone}
            onChange={onChange("phone")}
            autoComplete="tel"
            data-testid="fq-input-phone"
          />
          {errors.phone && <div className="fq-form-error">{errors.phone}</div>}
        </div>

        <button
          type="submit"
          className="fq-btn"
          disabled={submitting || !allAnswered}
          data-testid="fq-submit"
          style={{ marginTop: 6 }}
        >
          {submitting ? "Calculating your results…" : (
            <>Reveal my results <ChevronRight size={18} /></>
          )}
        </button>

        <div className="fq-form-trust">
          We respect your privacy. By signing up you agree to receive emails from Finlightenment.
          You can unsubscribe anytime.
        </div>
      </form>

      <div style={{ marginTop: 22 }}>
        <button className="fq-back" onClick={onBack} type="button" data-testid="fq-gate-back">
          <ArrowLeft size={16} /> Review last answer
        </button>
      </div>
    </div>
  );
}

/* =================================================================== */
/*                          Results view                                */
/* =================================================================== */
function ResultsView({ result, lead, onRestart }) {
  const tier = result.tier;
  const breakdown = result.pillarBreakdown;
  const focus = result.focusPillar;
  const strongest = result.strongestPillar;

  return (
    <div className="fq-fade-in" data-testid="fq-results">
      <div className="fq-results-hero">
        <div className="fq-results-eyebrow">
          Your Score · Tier {tier.id} of 3
        </div>
        <div className="fq-results-score" data-testid="fq-results-score">
          {result.total}<span className="fq-results-score-max">/{result.max}</span>
        </div>
        <h1 className="fq-results-tier-name" data-testid="fq-results-tier">{tier.name}</h1>
        <p className="fq-results-tagline">{tier.tagline}</p>
      </div>

      <div className="fq-results-body">
        <p style={{ marginTop: 0, fontSize: 16, lineHeight: 1.75 }}>
          <strong style={{ color: "var(--navy)" }}>Hi {(lead.name || "there").split(" ")[0]} —</strong> {tier.body}
        </p>
        {result.email_status === "success" && (
          <div className="fq-confirmation" data-testid="fq-email-confirm">
            <Mail size={16} /> A copy of your full personalized report has been sent to <strong style={{ color: "var(--navy)" }}>&nbsp;{lead.email}</strong>
          </div>
        )}
      </div>

      {/* Pillar breakdown */}
      <div className="fq-results-body" style={{ paddingBottom: 32 }}>
        <h3 className="fq-section-title">Your 6-Pillar Breakdown</h3>
        <p className="fq-pillar-feedback" style={{ marginBottom: 22, fontSize: 14.5 }}>
          Here's exactly where you're strong (<strong style={{ color: "var(--navy)" }}>{strongest.label}</strong>)
          and where focusing next will create the biggest shift (<strong style={{ color: "#b78228" }}>{focus.label}</strong>).
        </p>
        <div className="fq-pillars-grid">
          {breakdown.map((p) => (
            <div className="fq-pillar-card" key={p.key} data-testid={`fq-pillar-${p.key}`}>
              <div className={`fq-pillar-status bucket-${p.bucket}`}>
                {p.bucket === 0 && "Needs attention"}
                {p.bucket === 1 && "Making progress"}
                {p.bucket === 2 && "Solid foundation"}
              </div>
              <div className="fq-pillar-card-head">
                <h4 className="fq-pillar-card-name">{p.label}</h4>
                <span className="fq-pillar-card-score">{p.earned}/{p.max}</span>
              </div>
              <div className="fq-pillar-bar">
                <div className={`fq-pillar-bar-fill bucket-${p.bucket}`} style={{ width: `${p.pct}%` }} />
              </div>
              <p className="fq-pillar-feedback">{p.feedback}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Strengths + focus */}
      <div className="fq-results-body" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 28 }}>
        <div>
          <h3 className="fq-section-title">
            <Star size={18} style={{ color: "var(--gold)", marginRight: 8, verticalAlign: "-3px" }} />
            What you have going for you
          </h3>
          <ul className="fq-list">
            {tier.strengths.map((s) => (
              <li key={s}>
                <span className="fq-list-bullet"><CheckCircle2 size={13} strokeWidth={3} /></span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="fq-section-title">
            <TrendingUp size={18} style={{ color: "var(--navy)", marginRight: 8, verticalAlign: "-3px" }} />
            Where to focus next
          </h3>
          <ul className="fq-list focus">
            {tier.focus.map((f) => (
              <li key={f}>
                <span className="fq-list-arrow">→</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* CTA card */}
      <div className="fq-cta-card" data-testid="fq-cta-card">
        <div className="fq-cta-kicker">{tier.nextStepKicker}</div>
        <h3 className="fq-cta-headline">{tier.nextStepHeadline}</h3>
        <p className="fq-cta-sub">{tier.nextStepSub}</p>

        <div className="fq-pitch-card">
          <div className="fq-pitch-kicker">Financial Foundation Reset</div>
          <h4 className="fq-pitch-headline">
            Build a budget that actually works — and finally make your money support the life you want.
          </h4>
          <p className="fq-pitch-copy">
            <strong>The Financial Foundation Reset</strong> doesn't just change how you manage money — it changes how
            you experience your life, because money stops being the obstacle and starts becoming a tool.
          </p>
          <div className="fq-pitch-points">
            <div className="fq-pitch-point">Science-backed mindset work</div>
            <div className="fq-pitch-point">A practical budgeting system</div>
            <div className="fq-pitch-point">Live support to help it stick</div>
          </div>
          <p className="fq-pitch-copy">
            When a strong money mindset is paired with a practical budgeting system, taking control of your money — and
            building lasting financial success — becomes inevitable.
          </p>
          <p className="fq-pitch-copy">
            This three-week, guided experience helps you feel <strong>calm, confident, and in control</strong> of your money.
          </p>
          <p className="fq-pitch-copy" style={{ marginBottom: 0 }}>
            You don't need more advice. You need a foundation. And this is where it begins.
          </p>
        </div>

        <a
          href={RESET_CTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="fq-btn fq-btn-gold"
          data-testid="fq-cta-button"
        >
          <ShieldCheck size={18} />
          Click here → Financial Foundation Reset
        </a>
        <div className="fq-cta-foot">Small shifts. Strong foundation. Lasting change.</div>
      </div>

      {/* Education disclaimer */}
      <div className="fq-disclaimer" data-testid="fq-disclaimer">
        <div className="fq-disclaimer-icon">
          <Info size={18} strokeWidth={2.5} />
        </div>
        <div>
          <strong>This is financial education — not financial advice.</strong>
          <p style={{ margin: "4px 0 0", fontSize: 13.5, lineHeight: 1.65 }}>
            The results, scores, and recommendations above are general educational tools based on your self-reported answers.
            They are not personalized to your full financial picture. For decisions involving taxes, investments, insurance, estate
            planning, or any other specific aspect of your finances, please consult a qualified, licensed professional
            (CPA, CFP®, attorney, or licensed insurance agent) before taking action.
          </p>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button className="fq-btn fq-btn-ghost" onClick={onRestart} data-testid="fq-restart">
          Take the quiz again
        </button>
      </div>
    </div>
  );
}
